import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import EmptyState from "../../../components/EmptyState";
import CausesList from "../../trends/components/CausesList";
import MoodBalanceChart from "../../../shared/charts/moodBalance/MoodBalanceChart";
import { useTrendsView } from "../../trends/hooks/useTrendsView";
import { maxBarHeight } from "../../trends/components/constants";
import BarChart from "../../trends/components/BarChart";
import PeriodSelector from "../../trends/components/PeriodSelector";
import "../../trends/components/Trends.module.css";
import styles from "../../trends/components/Trends.module.css";
import {
  DEMO_STATUS_CAPS,
  getDemoTeamByName,
  TeamDemoPatternSubline,
} from "../../demo/teamDemoShared";
import demoStyles from "../../teams/components/TeamSettings.module.css";

/**
 * Demo: same layout as Trends, with team name from ?team= (navigated from TeamDemo).
 */
export default function TeamWeeklyOverViewCopy() {
  const [searchParams] = useSearchParams();
  const teamName = searchParams.get("team")?.trim() || "TeamDetails";
  const demoTeam = getDemoTeamByName(teamName);

  const {
    showCauses,
    selectedCauseView,
    selectedPeriod,
    totalMoods,
    balancePieData,
    modifiedBalancePieData,
    burnoutCauses,
    balancedCauses,
    barChartData,
    moodPercentages,
    weekdayLabels,
    availableWeeks,
    selectedWeek,
    onWeekChange,
    insightsGranularity,
    setInsightsGranularity,
    availableMonths,
    selectedMonth,
    onMonthChange,
  } = useTrendsView();

  const navigate = useNavigate();

  return (
    <div className={styles["trends-page-container"]}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "16px",
          gap: 6,
        }}
      >
        <span
          style={{
            fontSize: "1.05rem",
            fontWeight: 600,
            color: "#c6531a",
            textAlign: "center",
          }}
        >
          {teamName}
        </span>
        {demoTeam && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              marginTop: 4,
            }}
          >
            <span
              className={`${demoStyles["team-demo-card-badge"]} ${demoStyles[`team-demo-status-${demoTeam.status}`]}`}
            >
              {DEMO_STATUS_CAPS[demoTeam.status]}
            </span>
            <TeamDemoPatternSubline pattern={demoTeam.pattern} />
          </div>
        )}
      </div>
      <div className={styles["content-container"]}>
        {localStorage.getItem("showFullInsightsForAnonymous") === "true" && (
          <PeriodSelector
            insightsGranularity={insightsGranularity}
            setInsightsGranularity={setInsightsGranularity}
            availableWeeks={availableWeeks ?? []}
            selectedWeek={selectedWeek}
            onWeekChange={onWeekChange}
            availableMonths={availableMonths ?? []}
            selectedMonth={selectedMonth}
            onMonthChange={onMonthChange}
          />
        )}

        <div className={styles["challenged-heading-wrap"]}>
          <h2>Insights</h2>
        </div>

        <br />

        <h3>Feelings</h3>
        {showCauses ? (
          <>
            {totalMoods > 0 ? (
              <>
                <h2 className={styles["top-causes-title"]}>
                  Burned out feelings
                </h2>
                <MoodBalanceChart
                  balancePieData={modifiedBalancePieData}
                  totalMoods={totalMoods}
                />
              </>
            ) : (
              <EmptyState title="No mood data available" />
            )}

            {burnoutCauses ? Object.keys(burnoutCauses).length : 0}
            <CausesList filteredCauses={burnoutCauses} />
          </>
        ) : (
          <>
            {totalMoods > 0 ? (
              <>
                {selectedCauseView === "OVERVIEW" ? (
                  <BarChart
                    barChartData={barChartData}
                    weekdayLabels={weekdayLabels}
                    maxBarHeight={maxBarHeight}
                  />
                ) : (
                  <>
                    <h2 className={styles["top-causes-title"]}>
                      Balanced feelings
                    </h2>
                    <MoodBalanceChart
                      balancePieData={balancePieData}
                      totalMoods={totalMoods}
                    />
                  </>
                )}
              </>
            ) : (
              <EmptyState title="No mood data available" />
            )}

            <CausesList filteredCauses={balancedCauses} />
          </>
        )}
      </div>

      <button
        type="button"
        className="proceed-button"
        onClick={() => {
          navigate("/app/demo-team-invite");
        }}
      >
        Back to teams
      </button>
    </div>
  );
}
